import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FirestoreService } from './firestore/firestore.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/mergeMap';

import { Globals, Ref } from '../globals';
import { UserService } from './user.service';

export interface PurchaseRequest {
  active: boolean;
  approved1: string;
  approved2: string;
  folio: number;
  notes: string;
  ordered: any;
  productsInRequest: ProductsInRequest[];
  requesterRef: any;
  productionOrderRef: any;
  deliveryRequiredDate: Date;
  supplierRef: Ref;
  canceled: boolean;
  cancelReasons: {datetime: string, msg: string}[];
}

export interface ProductsInRequest {
  productRef: any;
  quantity: number;
}

export interface PurchaseRequestId extends PurchaseRequest {
  id: string;
}

@Injectable()
export class PurchaseRequestService {

  collection: string;
  purchaseOrderRef: string;
  usersRef: string;
  productionOrderRef: string;
  prCmpaCollection: string;

  constructor(
    private afh: FirestoreService,
    private userService: UserService,
    private router: Router,
  ) {
    this.collection = Globals.PURCHASE_REQUEST_COLLECTION;
    this.purchaseOrderRef = Globals.PURCHASE_ORDER_COLLECTION;
    this.usersRef = Globals.USER_COLLECTION;
    this.productionOrderRef = Globals.PRODUCTION_ORDER_COLLECTION;
    this.prCmpaCollection = Globals.PRCMPA_COLLECTION;
  }

  /**
   * Get all purchaseRequests
   * @return {Observable} Array of purchhaseRequests
   */
  getPurchaseRequests({excludeOrdered = false} = {}) {
    return this.afh.colWithIds$(this.collection, ref => {
      if(excludeOrdered) return ref.orderBy('createdAt', 'desc').where('active', '==', true).where('ordered', '==', false);
      return ref.orderBy('createdAt', 'desc').where('active', '==', true);
    }).map(documents => {
      return documents.map(document => {
        const data = {
          date: document.createdAt,
          name: document.folio,
          requester: document.requesterRef.id,
          productionOrder: document.productionOrderRef && document.productionOrderRef.id || '',
          ...document
        }
        return data;
      })
    })
  }

  /**
   * Get purchaseRequest with references
   * @param {string} id id of the purchaseRequest
   * @return {Observable} of purchhaseRequest
   */
  getPurchaseRequest(id: string) {
    return this.afh.docWithIds$(`${this.collection}/${id}`).map(document => {
      const requesterRef = this.userService.getUser(document.requesterRef.id);
      const productionOrderRef = document.productionOrderRef ? this.afh.docWithIds$(document.productionOrderRef.path) : undefined;
      return Observable.combineLatest(
        requesterRef,
        productionOrderRef || requesterRef,
        (requesterData, productionOrderData) => {
          return {
            requesterObj: requesterData,
            productionOrder: document.productionOrderRef ? productionOrderData : undefined,
            ...document
          }
        });
    }).mergeMap(purchaseRequestDocuments => Observable.combineLatest(purchaseRequestDocuments));
  }

  /**
   * Add a new purchaseRequest
   * @param {PurchaseRequest} purchaseRequest - The data of the purchaseRequest
   */
  addPurchaseRequest(purchaseRequest: PurchaseRequest){
    purchaseRequest.requesterRef = this.afh.doc(`${this.usersRef}/${purchaseRequest.requesterRef}`).ref;
    if(purchaseRequest.productionOrderRef) purchaseRequest.productionOrderRef = this.afh.doc(`${this.productionOrderRef}/${purchaseRequest.productionOrderRef}`).ref;
    purchaseRequest.ordered = false;

    this.afh.add(`${this.collection}`, purchaseRequest);
  }

  /**
   * Update purchaseRequest
   * @param {PurchaseRequest} purchaseRequest - The data of the purchaseRequest
   */
  updatePurchaseRequest(id: string, purchaseRequest: any){
    if(purchaseRequest.productionOrderRef) purchaseRequest.productionOrderRef = this.afh.doc(`${this.productionOrderRef}/${purchaseRequest.productionOrderRef}`).ref;
    this.afh.update(`${this.collection}/${id}`, purchaseRequest);
  }
  
  /**
   * Submit 1 approvation to the purchaseRequest
   * @param {string} id - The id of the purchaseRequest
   * @param {number} approvationNumber - Eather 1 or 2, the number of the aprovation
   * @param {string} userId - The id of the user that aproves the purchaseRequest
   */
  approvePurchaseRequest(id: string, approvationNumber: number, userId: string) {
    const userRef = this.afh.doc(`${this.usersRef}/${userId}`).ref;
    let data: {};
    if(approvationNumber == 1) {
      data = {
        approved1: userRef
      };
    } else if (approvationNumber == 2) {
      data = {
        approved2: userRef
      };
    }
    this.afh.update(`${this.collection}/${id}`, data);
  }

  /**
   * Add a reference of the purchaseOrder that corresponds to this purchaseRequest
   * @param {string} id - The id of the purchaseRequest
   * @param {string} orderId - The id of the purchaseOrder
   */
  orderPurchaseRequest(id: string, orderId: string) {
    const orderRef = this.afh.doc(`${this.purchaseOrderRef}/${orderId}`).ref;
    const data = {
      ordered: orderRef
    }
    this.afh.update(`${this.collection}/${id}`, data)
  }

  /**
   * Delete current purchaseRequest
   * @param {string} id - The id of the purchaseRequest
   */
  deletePurchaseRequest(id: string) {
    this.afh.delete(`${this.collection}/${id}`);
  }

}
